import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Crown, Check, Heart, Fire, Wallet as WalletIcon } from "@phosphor-icons/react";

const CYCLE_LABEL = { monthly: "mo", quarterly: "3mo", semiannual: "6mo", annual: "yr" };

export default function MatchPlans() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");

  const { data: plansData } = useQuery({ queryKey: ["match-plans"], queryFn: async () => (await api.get("/match/plans")).data });
  const { data: subData } = useQuery({ queryKey: ["match-subscription"], queryFn: async () => (await api.get("/match/subscription")).data });
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });

  const plans = plansData?.plans || [];
  const boost = plansData?.boost;
  const cycles = plansData?.cycles || ["monthly", "annual"];
  const current = subData?.entitlements?.plan_key || "free";
  const boostState = subData?.boost;

  const subscribe = useMutation({
    mutationFn: async (plan_key) => (await api.post("/match/subscribe", { plan_key, cycle })).data,
    onSuccess: (res) => {
      toast.success(`You're on ${res.entitlements.plan_name}!${res.included_boosts ? ` +${res.included_boosts} Boost(s) added.` : ""}`);
      qc.invalidateQueries({ queryKey: ["match-subscription"] });
      qc.invalidateQueries({ queryKey: ["match-me"] }); qc.invalidateQueries({ queryKey: ["wallet"] });
    },
    onError: (e) => { const msg = apiError(e); toast.error(msg); if (String(msg).includes("wallet")) navigate("/app/wallet"); },
  });

  const buyBoost = useMutation({
    mutationFn: async (qty) => (await api.post("/match/boost/buy", { qty })).data,
    onSuccess: () => { toast.success("Boosts added!"); qc.invalidateQueries({ queryKey: ["match-subscription"] }); qc.invalidateQueries({ queryKey: ["wallet"] }); },
    onError: (e) => { const msg = apiError(e); toast.error(msg); if (String(msg).includes("wallet")) navigate("/app/wallet"); },
  });
  const activateBoost = useMutation({
    mutationFn: async () => (await api.post("/match/boost/activate", {})).data,
    onSuccess: () => { toast.success("Boost activated — you'll be seen by more people!"); qc.invalidateQueries({ queryKey: ["match-subscription"] }); qc.invalidateQueries({ queryKey: ["match-me"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-8" data-testid="match-plans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Plans are billed from your <strong>iYeneh Wallet</strong> · Balance: {wallet ? money(wallet.balance_cents, wallet.currency) : "—"}</p>
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm">
          {cycles.map((c) => (
            <button key={c} data-testid={`match-cycle-${c}`} onClick={() => setCycle(c)}
              className={`rounded-full px-3.5 py-1.5 font-medium capitalize transition-colors ${cycle === c ? "bg-rose-500 text-white" : "text-muted-foreground"}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => {
          const price = p.pricing?.[cycle] ?? p.price_cents;
          const isCurrent = current === p.key;
          return (
            <div key={p.key} data-testid={`match-plan-${p.key}`}
              className={`flex flex-col rounded-3xl border bg-card p-5 ${p.popular ? "border-rose-500 ring-2 ring-rose-500/30" : "border-border"}`}>
              {p.badge && <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-rose-500/15 px-2.5 py-0.5 text-xs font-semibold text-rose-500"><Crown size={12} weight="fill" /> {p.badge}</span>}
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <p className="mt-1 min-h-[2.5rem] text-xs text-muted-foreground">{p.tagline}</p>
              <div className="mt-3">
                {price > 0 ? <><span className="font-display text-3xl font-semibold">{money(price)}</span><span className="text-sm text-muted-foreground">/{CYCLE_LABEL[cycle]}</span></>
                  : <span className="font-display text-3xl font-semibold">Free</span>}
              </div>
              <ul className="mt-4 flex-1 space-y-2">
                {(p.features || []).slice(0, 8).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><Check size={16} weight="bold" className="mt-0.5 shrink-0 text-rose-500" /><span>{f}</span></li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled data-testid={`match-plan-current-${p.key}`} className="mt-5 rounded-full border border-rose-500 bg-rose-500/10 py-2.5 text-sm font-semibold text-rose-500">Current plan</button>
              ) : (
                <button data-testid={`match-plan-choose-${p.key}`} disabled={subscribe.isPending} onClick={() => subscribe.mutate(p.key)}
                  className="mt-5 rounded-full bg-rose-500 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
                  {subscribe.isPending ? "…" : price > 0 ? "Choose plan" : "Switch to Free"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {boost && (
        <div className="rounded-3xl border border-orange-200 bg-gradient-to-b from-orange-50 to-card p-5 dark:border-orange-900/40 dark:from-orange-950/20" data-testid="match-boost">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white"><Fire size={24} weight="fill" /></div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold">Profile Boost</h3>
              <p className="text-xs text-muted-foreground">{boost.note}</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-semibold" data-testid="boost-balance">{boostState?.remaining || 0} left</p>
              {boostState?.active ? <span className="text-xs font-semibold text-orange-500">Active now</span>
                : <button data-testid="boost-activate" disabled={activateBoost.isPending || !(boostState?.remaining > 0)} onClick={() => activateBoost.mutate()} className="text-xs font-semibold text-orange-500 disabled:opacity-40">Activate</button>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(boost.options || []).map((o) => (
              <button key={o.qty} data-testid={`boost-buy-${o.qty}`} disabled={buyBoost.isPending} onClick={() => buyBoost.mutate(o.qty)}
                className="flex items-center gap-2 rounded-full border border-orange-300 bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-orange-500 dark:border-orange-900/60">
                <Fire size={14} weight="fill" className="text-orange-500" /> {o.qty} Boost{o.qty > 1 ? "s" : ""} · {money(o.cents)}
              </button>
            ))}
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><WalletIcon size={13} /> Charged to your iYeneh Wallet. Boost only increases legitimate discovery visibility.</p>
        </div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"><Heart size={13} weight="fill" className="text-rose-500" /> Safety features (block, report, unmatch, privacy) are always free on every plan.</p>
    </div>
  );
}
